/**
 * Gráficos do protótipo, sobre o `Chart` da Kumo (ECharts).
 *
 * As três regras de apresentação do projeto — percentis, eixo de concorrência
 * logarítmico e linha do alvo — são impostas em `lib/chart-options.ts`, testadas sem
 * desenhar nada. Este arquivo só resolve as cores do modo corrente e entrega as opções.
 */

import { useMemo } from "react";
import * as echarts from "echarts";
import type { EChartsOption } from "echarts";
import { Chart, ChartPalette, type KumoChartOption } from "@cloudflare/kumo";
import {
  histogramOption,
  lineChartOption,
  type ChartColors,
  type HistogramBar,
  type SeriesPoint,
} from "../lib/chart-options.js";
import { useThemeMode } from "./ThemeMode.js";

export type { SeriesPoint };

/** Quantidade de cores categóricas da paleta da Kumo. */
const CATEGORICAL_COLORS = 6;

/**
 * Cores do modo corrente.
 *
 * As séries usam a paleta categórica da Kumo, que já foi desenhada para contraste nos
 * dois modos; eixo e linha do alvo ficam em cinza, para nunca competirem com o dado.
 */
function useChartColors(): ChartColors {
  const { isDark } = useThemeMode();
  return useMemo(
    () => ({
      series: Array.from({ length: CATEGORICAL_COLORS }, (_, index) => ChartPalette.categorical(index, isDark)),
      target: isDark ? "#a3a3a3" : "#737373",
      axisText: isDark ? "#a3a3a3" : "#525252",
      gridLine: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)",
    }),
    [isDark],
  );
}

/** Entradas da moldura comum. */
interface ChartFrameProps {
  readonly option: EChartsOption;
  readonly height: number;
}

/** Moldura comum: o gráfico da Kumo no modo corrente. */
function ChartFrame({ option, height }: ChartFrameProps): React.ReactElement {
  const { isDark } = useThemeMode();
  return <Chart echarts={echarts} options={option as KumoChartOption} height={height} isDarkMode={isDark} />;
}

/** Entradas do gráfico de latência ao longo do tempo. */
interface LatencyOverTimeProps {
  readonly data: readonly SeriesPoint[];
  /** Valor do alvo, desenhado como linha de referência. */
  readonly targetValue: number;
  readonly targetLabel: string;
  readonly yTitle: string;
  readonly height?: number;
}

/**
 * Série temporal de latência, com a linha do alvo sempre presente.
 *
 * A linha do alvo torna o goodput legível como área, sem exigir que o leitor faça a
 * conta — é a razão de ela não ser opcional.
 */
export function LatencyOverTimeChart({
  data,
  targetValue,
  targetLabel,
  yTitle,
  height = 300,
}: LatencyOverTimeProps): React.ReactElement {
  const colors = useChartColors();
  const option = useMemo(
    () =>
      lineChartOption({
        data,
        xTitle: "tempo",
        yTitle,
        xAxis: "value",
        target: { value: targetValue, label: targetLabel },
        colors,
      }),
    [data, yTitle, targetValue, targetLabel, colors],
  );
  return <ChartFrame option={option} height={height} />;
}

/** Entradas da curva de concorrência contra latência. */
interface ConcurrencyCurveProps {
  readonly data: readonly SeriesPoint[];
  readonly targetValue: number;
  readonly targetLabel: string;
  readonly yTitle: string;
  readonly height?: number;
}

/**
 * Curva de concorrência, com eixo horizontal logarítmico.
 *
 * A escala logarítmica não é preferência: em escala linear, a faixa de 1 a 128 comprime
 * justamente o joelho da curva — que é o único ponto de interesse do gráfico.
 */
export function ConcurrencyCurveChart({
  data,
  targetValue,
  targetLabel,
  yTitle,
  height = 360,
}: ConcurrencyCurveProps): React.ReactElement {
  const colors = useChartColors();
  const option = useMemo(
    () =>
      lineChartOption({
        data,
        xTitle: "concorrência (escala logarítmica)",
        yTitle,
        xAxis: "log",
        target: { value: targetValue, label: targetLabel },
        colors,
        smooth: true,
        showPoints: true,
      }),
    [data, yTitle, targetValue, targetLabel, colors],
  );
  return <ChartFrame option={option} height={height} />;
}

/** Entradas do gráfico de evolução por turno. */
interface TurnSeriesProps {
  readonly data: readonly SeriesPoint[];
  readonly yTitle: string;
  readonly height?: number;
}

/**
 * Evolução de uma métrica turno a turno dentro da conversa.
 *
 * É o gráfico que torna visível o cache de prefixo trabalhando: a curva subindo do turno
 * 1 ao 10 é a prova de que cada turno reaproveita o histórico anterior.
 */
export function TurnSeriesChart({ data, yTitle, height = 280 }: TurnSeriesProps): React.ReactElement {
  const colors = useChartColors();
  const option = useMemo(
    () =>
      lineChartOption({
        data,
        xTitle: "turno da conversa",
        yTitle,
        xAxis: "value",
        target: null,
        colors,
        showPoints: true,
      }),
    [data, yTitle, colors],
  );
  return <ChartFrame option={option} height={height} />;
}

/** Entradas do histograma de latência. */
interface HistogramChartProps {
  readonly bars: readonly HistogramBar[];
  readonly xTitle: string;
  readonly yTitle: string;
  readonly height?: number;
}

/**
 * Histograma com as faixas coloridas conforme o alvo.
 *
 * Azul atende, vermelho não. A cor de "acima do alvo" vem da paleta semântica da Kumo,
 * a mesma usada nos avisos de erro, para que o vermelho signifique a mesma coisa em
 * toda a tela.
 */
export function HistogramChart({ bars, xTitle, yTitle, height = 300 }: HistogramChartProps): React.ReactElement {
  const { isDark } = useThemeMode();
  const colors = useChartColors();
  const option = useMemo(
    () =>
      histogramOption({
        bars,
        xTitle,
        yTitle,
        withinLabel: "atende o alvo",
        aboveLabel: "acima do alvo",
        withinColor: ChartPalette.categorical(0, isDark),
        aboveColor: ChartPalette.semantic("Attention", isDark),
        colors,
      }),
    [bars, xTitle, yTitle, colors, isDark],
  );
  return <ChartFrame option={option} height={height} />;
}
